import { z } from "zod";

/**
 * A storefront's legal identity: who the Terms of Service and Privacy Policy bind the shopper
 * to, how to reach them, and which law the terms are read under. Everything else those pages
 * name — the brand, the site, the currency — is already on the resolved config; these six are
 * the facts the config does not otherwise carry. Stored whole on
 * `storefront_configs.legal` (NULL = inherit) and `platform_settings.legal` (NULL = the
 * platform's own identity in code), and parsed on every read.
 */
export const storefrontLegalSchema = z.object({
  /** The contracting entity as it appears in the terms ("Loot Labs, Inc."). */
  legalName: z.string().trim().min(1).max(200),
  /** Where shoppers write with questions, disputes and account requests. */
  supportEmail: z.email().max(320),
  /** Where notices of claimed infringement go; "" = the support address. */
  legalEmail: z.union([z.literal(""), z.email().max(320)]),
  /** One line: where a shopper mails the arbitration opt-out and where the entity sits. */
  postalAddress: z.string().trim().min(1).max(500),
  /** The phrase after "the laws of" ("the State of Washington, United States of America"). */
  governingLaw: z.string().trim().min(1).max(200),
  /** Where an in-person arbitration would sit ("King County, Washington"). */
  arbitrationVenue: z.string().trim().min(1).max(200),
});
export type StorefrontLegal = z.infer<typeof storefrontLegalSchema>;

/**
 * The `legal` block of the resolved storefront config: the stored shape with the one fallback
 * applied, so the SPA reads `legalEmail` and never decides itself.
 */
export const publicStorefrontLegalSchema = storefrontLegalSchema.extend({
  legalEmail: z.email().max(320),
});
export type PublicStorefrontLegal = z.infer<typeof publicStorefrontLegalSchema>;

/** The stored identity as the shopper sees it: the legal address filled from the support one. */
export function toPublicStorefrontLegal(legal: StorefrontLegal): PublicStorefrontLegal {
  return { ...legal, legalEmail: legal.legalEmail || legal.supportEmail };
}

/** The footer's closing line: copyright, the platform, fairness and the trademark notice. */
export function storefrontFooterLegal({
  year,
  legalName,
}: {
  year: number;
  legalName: string;
}): string {
  return `© ${year} ${legalName}. All rights reserved. Powered by Loot Labs. Every open is provably fair and verifiable on the fairness page. All product and company names are trademarks or registered trademarks of their respective holders; use of them does not imply any affiliation with or endorsement by them.`;
}

/**
 * The policy pages' titles, as the pages show them, the footer links them and the sign-in consent
 * line cites them: platform words (the rule is in `storefrontCopy/shared.ts`).
 */
export const STOREFRONT_POLICY_TITLES = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
} as const;

/** The footer's control that reopens the cookie banner's choices: platform words too. */
export const COOKIE_PREFERENCES_LABEL = "Cookie preferences";
